/**
 * Party Manager Domain - Zodios API Definitions
 * 
 * Type-safe API contracts using Zodios to ensure backend, frontend,
 * and MCP clients always stay in sync. Single source of truth!
 */

import { makeApi } from '@zodios/core';
import { z } from 'zod';

// Zod schemas based on existing Party Manager contracts

// Person Schemas
export const PersonSummarySchema = z.object({
  personId: z.string().describe('Unique person identifier'),
  email: z.string().email().describe('Person email address'),
  firstName: z.string().describe('Person first name'),
  lastName: z.string().describe('Person last name'),
  displayName: z.string().describe('Person display name'),
  roleType: z.enum(['Human', 'AiAgent']).describe('Type of person'),
  isActive: z.boolean().describe('Whether person is active'),
  contactInfo: z.object({
    phone: z.string().optional(),
    preferredName: z.string().optional()
  }),
  professionalInfo: z.object({
    department: z.string().optional(),
    jobTitle: z.string().optional(),
    managerId: z.string().optional(),
    managerName: z.string().optional(),
    location: z.string().optional(),
    hireDate: z.string().optional() // ISO date
  }),
  organizationContext: z.object({
    organizationId: z.string(),
    organizationName: z.string(),
    organizationType: z.string()
  }).optional(),
  profileMetrics: z.object({
    directReportsCount: z.number(),
    accountCreatedAt: z.string(),
    lastLoginAt: z.string().optional(),
    totalLoginCount: z.number()
  }),
  securityProfile: z.object({
    mfaEnabled: z.boolean(),
    accountLocked: z.boolean(),
    failedLoginAttempts: z.number(),
    lastPasswordChange: z.string().optional()
  }),
  auditInfo: z.object({
    createdAt: z.string(),
    updatedAt: z.string()
  })
});

export const PersonQueryCriteriaSchema = z.object({
  searchTerm: z.string().optional().describe('Search across name, email, and other text fields'),
  email: z.string().optional().describe('Exact email match'),
  firstName: z.string().optional().describe('Exact or partial first name'),
  lastName: z.string().optional().describe('Exact or partial last name'),
  roleType: z.enum(['Human', 'AiAgent', 'all']).optional(),
  isActive: z.boolean().optional(),
  department: z.string().optional(),
  jobTitle: z.string().optional(),
  managerId: z.string().optional(),
  organizationId: z.string().optional(),
  hasManager: z.boolean().optional(),
  hasDirectReports: z.boolean().optional(),
  locationFilter: z.object({
    country: z.string().optional(),
    region: z.string().optional(),
    city: z.string().optional()
  }).optional(),
  securityFilters: z.object({
    mfaEnabled: z.boolean().optional(),
    accountLocked: z.boolean().optional(),
    hasFailedLogins: z.boolean().optional(),
    passwordExpired: z.boolean().optional()
  }).optional(),
  pagination: z.object({
    limit: z.number().optional(),
    offset: z.number().optional(),
    sortBy: z.enum(['name', 'email', 'createdAt', 'lastLogin', 'department']).optional(),
    sortOrder: z.enum(['asc', 'desc']).optional()
  }).optional()
});

export const CreatePersonRequestSchema = z.object({
  email: z.string().email().describe('Person email address'),
  firstName: z.string().describe('Person first name'),
  lastName: z.string().describe('Person last name'),
  roleType: z.enum(['Human', 'AiAgent']).describe('Type of person'),
  organizationId: z.string().optional().describe('Organization ID'),
  departmentId: z.string().optional().describe('Department ID'),
  jobTitle: z.string().optional().describe('Job title'),
  managerId: z.string().optional().describe('Manager person ID'),
  contactInfo: z.object({
    phone: z.string().optional(),
    preferredName: z.string().optional()
  }).optional()
});

// Organization Schemas
export const OrganizationSummarySchema = z.object({
  organizationId: z.string().describe('Unique organization identifier'),
  legalName: z.string().describe('Legal organization name'),
  displayName: z.string().optional().describe('Display name'),
  tradingName: z.string().optional().describe('Trading name'),
  businessProfile: z.object({
    industry: z.string(),
    organizationType: z.string(),
    organizationSize: z.enum(['startup', 'small', 'medium', 'large', 'enterprise']),
    foundedYear: z.number().optional()
  }),
  contactDetails: z.object({
    primaryEmail: z.string().optional(),
    primaryPhone: z.string().optional(),
    website: z.string().optional()
  }),
  locationInfo: z.object({
    headquartersCountry: z.string().optional(),
    headquartersRegion: z.string().optional(),
    headquartersCity: z.string().optional(),
    operatingRegions: z.array(z.string())
  }),
  employeeMetrics: z.object({
    totalEmployees: z.number(),
    humanEmployees: z.number(),
    aiAgents: z.number(),
    recentHires: z.number(),
    departmentCount: z.number()
  }),
  operationalStatus: z.object({
    isActive: z.boolean(),
    operationalSince: z.string().optional(),
    lastActivityDate: z.string().optional()
  }),
  auditInfo: z.object({
    createdAt: z.string(),
    updatedAt: z.string()
  })
});

export const OrganizationQueryCriteriaSchema = z.object({
  searchTerm: z.string().optional(),
  legalName: z.string().optional(),
  industry: z.string().optional(),
  organizationType: z.string().optional(),
  organizationSize: z.enum(['startup', 'small', 'medium', 'large', 'enterprise', 'all']).optional(),
  isActive: z.boolean().optional(),
  pagination: z.object({
    limit: z.number().optional(),
    offset: z.number().optional(),
    sortBy: z.enum(['name', 'industry', 'size', 'createdAt', 'employeeCount']).optional(),
    sortOrder: z.enum(['asc', 'desc']).optional()
  }).optional()
});

// Authentication Schemas
export const AuthenticationSummarySchema = z.object({
  personId: z.string(),
  authenticationId: z.string(),
  email: z.string(),
  accountStatus: z.object({
    status: z.enum(['active', 'locked', 'suspended', 'pending_activation']),
    isEmailVerified: z.boolean(),
    accountCreationDate: z.string(),
    lastStatusChange: z.string().optional()
  }),
  sessionMetrics: z.object({
    hasActiveSession: z.boolean(),
    activeSessionCount: z.number(),
    lastLoginAt: z.string().optional(),
    lastLoginIP: z.string().optional(),
    totalLoginCount: z.number(),
    averageSessionDuration: z.number()
  }),
  securitySettings: z.object({
    mfaEnabled: z.boolean(),
    mfaMethod: z.enum(['totp', 'sms', 'email']).optional(),
    passwordLastChanged: z.string().optional(),
    passwordStrength: z.enum(['weak', 'medium', 'strong']),
    failedLoginAttempts: z.number(),
    lockoutExpiresAt: z.string().optional()
  }),
  auditInfo: z.object({
    createdAt: z.string(),
    updatedAt: z.string()
  })
});

// Party Manager Domain API using Zodios
export const partyManagerApi = makeApi([
  // Person Management
  {
    method: 'get',
    path: '/api/persons',
    alias: 'getPersons',
    description: 'Get persons with optional filtering and pagination',
    parameters: [
      {
        name: 'query',
        type: 'Query',
        schema: PersonQueryCriteriaSchema
      }
    ],
    response: z.object({
      items: z.array(PersonSummarySchema),
      total: z.number(),
      hasMore: z.boolean(),
      pagination: z.object({
        limit: z.number(),
        offset: z.number(),
        totalPages: z.number()
      })
    })
  },
  {
    method: 'get',
    path: '/api/persons/:id',
    alias: 'getPerson',
    description: 'Get a specific person by ID',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string()
      }
    ],
    response: PersonSummarySchema
  },
  {
    method: 'post',
    path: '/api/persons',
    alias: 'createPerson',
    description: 'Create a new person',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreatePersonRequestSchema
      }
    ],
    response: PersonSummarySchema
  },
  {
    method: 'put',
    path: '/api/persons/:id',
    alias: 'updatePerson',
    description: 'Update an existing person',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string()
      },
      {
        name: 'body',
        type: 'Body',
        schema: CreatePersonRequestSchema.partial()
      }
    ],
    response: PersonSummarySchema
  },
  
  // Organization Management
  {
    method: 'get',
    path: '/api/organizations',
    alias: 'getOrganizations',
    description: 'Get organizations with optional filtering',
    parameters: [
      {
        name: 'query',
        type: 'Query',
        schema: OrganizationQueryCriteriaSchema
      }
    ],
    response: z.object({
      items: z.array(OrganizationSummarySchema),
      total: z.number(),
      hasMore: z.boolean()
    })
  },
  {
    method: 'get',
    path: '/api/organizations/:id',
    alias: 'getOrganization',
    description: 'Get a specific organization by ID',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string()
      }
    ],
    response: OrganizationSummarySchema
  },
  
  // Authentication Management
  {
    method: 'get',
    path: '/api/authentication',
    alias: 'getAuthenticationRecords',
    description: 'Get authentication records for security analysis',
    parameters: [
      {
        name: 'query',
        type: 'Query',
        schema: z.object({
          personId: z.string().optional(),
          email: z.string().optional(),
          accountStatus: z.enum(['active', 'locked', 'suspended', 'pending_activation', 'all']).optional(),
          organizationId: z.string().optional(),
          limit: z.number().optional(),
          offset: z.number().optional()
        })
      }
    ],
    response: z.object({
      items: z.array(AuthenticationSummarySchema),
      total: z.number(),
      hasMore: z.boolean()
    })
  },
  {
    method: 'get',
    path: '/api/authentication/:personId',
    alias: 'getAuthenticationByPerson',
    description: 'Get authentication record for a specific person',
    parameters: [
      {
        name: 'personId',
        type: 'Path',
        schema: z.string()
      }
    ],
    response: AuthenticationSummarySchema
  },
  
  // Health/Status endpoints
  {
    method: 'get',
    path: '/api/party-manager/health',
    alias: 'getHealthStatus',
    description: 'Get Party Manager domain health status',
    response: z.object({
      status: z.enum(['healthy', 'degraded', 'unhealthy']),
      version: z.string(),
      timestamp: z.string(),
      checks: z.object({
        database: z.boolean(),
        authentication: z.boolean(),
        cache: z.boolean()
      })
    })
  }
]);

// Export types inferred from Zodios API
export type PartyManagerApi = typeof partyManagerApi;
export type PersonSummary = z.infer<typeof PersonSummarySchema>;
export type PersonQueryCriteria = z.infer<typeof PersonQueryCriteriaSchema>;
export type CreatePersonRequest = z.infer<typeof CreatePersonRequestSchema>;
export type OrganizationSummary = z.infer<typeof OrganizationSummarySchema>;
export type OrganizationQueryCriteria = z.infer<typeof OrganizationQueryCriteriaSchema>;
export type AuthenticationSummary = z.infer<typeof AuthenticationSummarySchema>;